import { render } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import "../style.scss";
import { XRCheckOverlay } from "./components/Layout/XRCheckOverlay.tsx";
import { FadeTransition } from "./eyejack-web-core/src/ui/components/Transitions/Transitions.tsx";
import { Splash } from "./components/Layout/Splash.tsx";
import { useApp } from "./eyejack-web-core/src/ui/hooks/useApp.ts";
import { AppState } from "./types/AppState.ts";
import { AppBaseProvider } from "./eyejack-web-core/src/ui/context/app-context.tsx";
import emitter from "./eyejack-web-core/src/core/events/eventemitter";
import useUserDevice from "./eyejack-web-core/src/ui/hooks/useUserDevice.ts";
import useLocalization from "./eyejack-web-core/src/ui/hooks/useLocalization";
import useUserId from "./eyejack-web-core/src/ui/hooks/useUserId";
import useAnalytics from "./eyejack-web-core/src/ui/hooks/useAnalytics";
import clsx from "clsx";
import { IconButton, Modal, Spinner } from "./eyejack-web-core/src/ui/components/Elements/index.ts";
import OnboardingImg from "./assets/onboarding-img-2.webp";
import InfoButtonImg from "./assets/info-button.svg";
import events from "../events.ts";
import { useEventListener } from "./eyejack-web-core/src/ui/hooks/useEventListener.ts";

emitter.on("initialized", () => {
  emitter.emit("load");
});

export function App() {
  const [localizedSuccess, setLocalizedSuccess] = useState(0);
  const [totalLocalizedCount, setTotalLocalizedCount] = useState(0);
  const [contentLoaded, setContentLoaded] = useState(false);

  const { appState, setAppState } = useApp();

  const { localizeSuccess, localize, isLocalizing, localizeDuration } =
    useLocalization();

  const { device, isMobile } = useUserDevice();
  const userId = useUserId();

  const { initAnalytics, sendEvent } = useAnalytics(userId!);

  const handleFinishOnboarding = () => {
    localize();
    if (localizedSuccess === 0) {
      setAppState(AppState.CALIBRATING);
    } else {
      setAppState(AppState.ARTWORK_VIEWING);
      events.emit("play"); // Loads all content
    }
  };

  const handleStartOnboarding = () => {
    setAppState(AppState.ONBOARDING);
  };

  // handle events
  useEffect(() => {
    const handleXRStarted = () => {
      setAppState(AppState.TRANSITIONING_XR);

      // Slight delay to fade in without flicker
      setTimeout(() => {
        setAppState(AppState.ONBOARDING);
        console.log("UI: app loaded - calibrating");
      }, 1000);
    };

    const handleXRExited = () => {
      console.log("UI: app stopped");
      events.emit("stop");
      setLocalizedSuccess(0);
      setTotalLocalizedCount(0);
      setAppState(AppState.SPLASH);
    };

    const handleLocalized = (e: any) => {
      if (
        appState === AppState.CALIBRATING ||
        appState === AppState.ARTWORK_VIEWING
      ) {
        if (e.success) {
          if (localizedSuccess === 0) {
            events.emit("play");
            setAppState(AppState.ARTWORK_VIEWING);
          }

          setLocalizedSuccess((prev) => prev + 1);
        }
        setTotalLocalizedCount((prev) => prev + 1);
      }
    };

    const handleLoaded = () => {
      console.log("UI: loading complete");
      setContentLoaded(true);
    };

    emitter.on("localized", handleLocalized);
    emitter.on("xr-started", handleXRStarted);
    emitter.on("xr-exited", handleXRExited);
    emitter.on("loaded", handleLoaded);
    return () => {
      emitter.off("loaded", handleXRStarted);
      emitter.off("localized", handleLocalized);
      emitter.off("xr-exited", handleXRExited);
      emitter.off("loaded", handleLoaded);
    };
  }, [setAppState, appState, localizedSuccess]);

  const documentRef = useRef<Document>(document);
  useEventListener(
    "visibilitychange",
    () => {
      if (document.hidden) {
        // android only
        if (isMobile && device !== "AppClip") {
          setAppState(AppState.SPLASH);
        }
      }
    },
    documentRef
  );

  useEffect(() => {
    if (import.meta.env.DEV) return
    if (!userId || !device) return;
    const loadGA = async () => {
      await initAnalytics("G-48GJ3JGQY");
      if (device === "iOS" || device === "Android" || !isMobile) {
        sendEvent("page_view", {
          page_title: document.title,
          page_path: window.location.pathname,
        });
      }
    };

    loadGA();
  }, [userId, device, initAnalytics, sendEvent, isMobile]);

  return (
    <div
      className="h-[100dvh] w-full relative z-10
        "
    >
      <div
        className={clsx("", {
          "opacity-0 pointer-events-none":
            appState === AppState.SPLASH || appState === AppState.LOADING,
          "opacity-1 pointer-events-auto":
            appState !== AppState.LOADING && appState !== AppState.SPLASH,
        })}
      >
        <FadeTransition show={appState === AppState.TRANSITIONING_XR}>
          <div
            style={{
              height: "100vh",
              width: "100vw",
              position: "fixed",
              top: 0,
              left: 0,
              backgroundColor: "black",
            }}
          ></div>
        </FadeTransition>

        {(appState === AppState.CALIBRATING ||
          appState === AppState.ARTWORK_VIEWING) && (
          <div className="absolute top-[60px] left-4 text-md text-shadow">
            {" "}
            Localizations: {localizedSuccess}/{totalLocalizedCount}
            <br></br>Duration: {localizeDuration}s
          </div>
        )}
        <div className="absolute bottom-[calc(env(safe-area-inset-bottom)+1rem)] left-4 text-md centeredX">
          {/* <DebugMenu /> */}
        </div>
      </div>

      <FadeTransition show={appState === AppState.ARTWORK_VIEWING}>
        <div>
          <div className="absolute top-[50px] right-[20px]">
            <IconButton
              onClick={handleStartOnboarding}
              icon={<img src={InfoButtonImg} />}
            />
          </div>
        </div>
      </FadeTransition>

      <XRCheckOverlay />

      <FadeTransition
        show={appState === AppState.SPLASH || appState === AppState.LOADING}
      >
        <div className="h-full w-full bg-black">
          <Splash contentLoaded={contentLoaded} />
        </div>
      </FadeTransition>

      <FadeTransition show={appState === AppState.ONBOARDING}>
        <div>
          <Modal className="bg-black text-white max-h-[600px] max-w-[370px]">
            <div className="h-full flex flex-col justify-between xgap-1 py-10 px-8 ">
              <h2 className="font-inter-bold text-2xl tracking-[0.4px] leading-6">
                The Sea
              </h2>
              <p className="text-sm tracking-[0.4px] leading-[5px]">
                By Stephen Walker
              </p>
              <div className="pb-5"></div>
              <span className="block w-10 h-[1px] bg-[#B69059]" />
              <div className="py-4">
                <img
                  className="mx-auto max-w-[150px] w-full"
                  src={OnboardingImg}
                />
              </div>
              <p className="pb-4 text-2xl text-center text-white tracking-[0.4px] leading-[26px]">
                Point your device
                <br />
                at the sculpture
              </p>
              <button
                onClick={handleFinishOnboarding}
                className="mx-auto text-lg w-[242px] h-[56px] border-2  tracking-[1.8px] border-[#B48F59] active:bg-[#B48F59] active:border-[#B48F59]"
              >
                OK
              </button>
            </div>
          </Modal>
        </div>
      </FadeTransition>

      <FadeTransition show={appState === AppState.CALIBRATING}>
        <div className="w-full absolute bottom-0">
          <div
            className="absolute bottom-14 z-10  flex justify-center items-center xmax-w-[350px] w-full px-[30px] gap-2 "
            style={{
              gap: "2rem",
            }}
          >
            <Spinner size="xl" thickness="thin" className="ml-2" />
            <p className="text-white text-[24px] text-shadow tracking-[0.4px]">
              Searching..
            </p>
          </div>
        </div>
      </FadeTransition>
    </div>
  );
}

render(
  <AppBaseProvider>
    <App />
  </AppBaseProvider>,
  document.getElementById("app")!
);
